import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import api from '../../../utils/api';

const getDate = (value) => {
  if (!value) return null;
  const date = value?.toDate
    ? value.toDate()
    : new Date(value?._seconds !== undefined
      ? value._seconds * 1000
      : value?.seconds !== undefined
        ? value.seconds * 1000
        : value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatActivity = (activity) => {
  let icon = 'Activity';
  let color = 'text-muted-foreground';
  switch (activity.type) {
    case 'product_added':
    case 'product_created':
      icon = 'Plus';
      color = 'text-primary';
      break;
    case 'product_updated':
      icon = 'Edit';
      color = 'text-primary';
      break;
    case 'product_deleted':
      icon = 'Trash';
      color = 'text-destructive';
      break;
    case 'product_sold':
    case 'checkout_completed':
      icon = 'ShoppingCart';
      color = 'text-success';
      break;
    case 'purchase_order_created':
      icon = 'FileText';
      color = 'text-primary';
      break;
    case 'low_stock_alert':
      icon = 'AlertTriangle';
      color = 'text-warning';
      break;
    default:
      break;
  }

  return {
    ...activity,
    type: activity.type || 'unknown',
    title: activity.title || activity.type?.replace(/_/g, ' ') || 'N/A',
    description: activity.description || 'N/A',
    user: activity.userName || activity.userEmail || activity.userId || 'N/A',
    timestamp: getDate(activity.timestamp),
    icon,
    color
  };
};

const ActivityFeed = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/activity', { params: { limit: 10 } });
      if (!Array.isArray(data?.activities)) throw new Error('Activity response has an unexpected shape');
      setActivities(data.activities.map(formatActivity));
      setError('');
    } catch (requestError) {
      console.error('Failed to fetch activities:', requestError);
      setError('Failed to load recent activities from the backend.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
    const intervalId = setInterval(fetchActivities, 60000);
    return () => clearInterval(intervalId);
  }, [fetchActivities]);

  const activityTypes = useMemo(
    () => [...new Set(activities.map(activity => activity.type))].sort(),
    [activities]
  );
  const filteredActivities = filter === 'all'
    ? activities
    : activities.filter(activity => activity.type === filter);

  const formatTime = (date) => {
    if (!date) return 'N/A';
    const diffInMinutes = Math.floor((Date.now() - date.getTime()) / (1000 * 60));
    if (diffInMinutes < 0) return 'N/A';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-card">
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-foreground">Recent Activity</h2>
            <p className="text-xs sm:text-sm text-muted-foreground">Latest inventory movements and updates</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <select
                value={filter}
                onChange={event => setFilter(event.target.value)}
                className="appearance-none bg-background border border-border rounded-md py-2 sm:py-1 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 h-10 sm:h-8"
              >
                <option value="all">All Activities</option>
                {activityTypes.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
              </div>
            </div>
            <button
              aria-label="Refresh activities"
              className="p-2 h-10 w-10 sm:h-8 sm:w-8 hover:bg-muted rounded-lg transition-hover flex items-center justify-center"
              onClick={fetchActivities}
            >
              <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-[300px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-[300px] text-center p-4">
            <Icon name="AlertTriangle" size={24} className="text-warning mb-2" />
            <p role="alert" className="text-muted-foreground">{error}</p>
            <button className="mt-4 px-4 py-2 text-sm bg-muted rounded-md hover:bg-muted/80 h-10" onClick={fetchActivities}>
              Try Again
            </button>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[300px] text-center p-4">
            <Icon name="Inbox" size={24} className="text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No activities found</p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {filteredActivities.map(activity => (
              <div key={activity.id} className="flex items-start space-x-3 group p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className={`w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 ${activity.color}`}>
                  <Icon name={activity.icon} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-foreground group-hover:text-primary">{activity.title}</h4>
                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground whitespace-nowrap ml-2">
                      {formatTime(activity.timestamp)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{activity.description}</p>
                  <div className="flex items-center mt-2">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mr-1.5">
                      <Icon name="User" size={12} className="text-primary" />
                    </div>
                    <span className="text-xs text-muted-foreground">{activity.user}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border">
        <button
          onClick={() => navigate('/activity-log')}
          className="w-full text-sm text-primary hover:text-primary/80 font-medium py-2 h-10"
        >
          View All Activity
        </button>
      </div>
    </div>
  );
};

export default ActivityFeed;
